/**
 * The one sentence each audit event type reads as, wherever it is shown.
 *
 * @module components/shared/audit-event-labels
 */

import type { AuditEventType } from "@/server/audit-events";

export const AUDIT_EVENT_LABELS: Record<AuditEventType, string> = {
  "assessment.override-assessments-added": "Assessment override: assessments added",
  "assessment.override-assessments-removed": "Assessment override: assessments removed",
  "assessment.override-created": "Assessment override created",
  "assessment.override-reverted": "Assessment override reverted",
  "assessment.override-updated": "Assessment override updated",
  "assignment.created": "Assignment created",
  "edit-lease.acquired": "Edit lease acquired",
  "edit-lease.disconnected": "Edit lease disconnected",
  "edit-lease.released": "Edit lease released",
  "final-sign-off.recorded": "Final sign-off recorded",
  "impersonation.started": "Impersonation started",
  "impersonation.stopped": "Impersonation stopped",
  "invitation.created": "Invitation created",
  "invitation.reissued": "Invitation reissued",
  "login.identity-bound": "Sign-in identity linked",
  "login.invalid-token": "Sign-in rejected: invalid token",
  "login.success": "Signed in",
  "login.no-invite": "Sign-in refused: no invitation",
  "login.ambiguous-invite": "Sign-in refused: more than one invitation",
  "login.tenant-missing": "Sign-in refused: institution not found",
  "login.tenant-unavailable": "Sign-in refused: institution unavailable",
  "login.cross-tenant": "Sign-in refused: different directory",
  "login.identity-conflict": "Sign-in refused: account linked elsewhere",
  "login.alias-outside-tenant": "Sign-in refused: alternate address for an account used elsewhere",
  "login.alias-address-taken": "Sign-in refused: alternate address held by another account",
  "login.invite-withdrawn": "Sign-in refused: invitation withdrawn or readdressed during sign-in",
  "login.unknown-user": "Sign-in refused: unknown user",
  "login.deactivated": "Sign-in refused: account deactivated",
  "module-owner.assigned": "Module owner assigned",
  "module.created": "Module created",
  "module.reviewed": "Module reviewed",
  "module.saved": "Module saved",
  "module.submitted": "Module submitted for review",
  "programme-responsibility.assigned": "Programme responsibility assigned",
  "programme.amendment-opened": "Programme amendment opened",
  "programme.approval-candidate-created": "Programme approval candidate created",
  "programme.approved": "Programme approved",
  "programme.change-case-approved": "Change case approved",
  "programme.change-case-discarded": "Change case discarded",
  "programme.change-case-identity-impact-decided": "Change case identity impact decided",
  "programme.change-case-internal-review-completed": "Change case internal review completed",
  "programme.change-case-reviewed": "Change case reviewed",
  "programme.change-case-submitted": "Change case submitted",
  "programme.change-case-updated": "Change case updated",
  "programme.change-case-withdrawn": "Change case withdrawn",
  "programme.change-team-confirmed": "Change team confirmed",
  "programme.change-team-reset": "Change team reset",
  "programme.create": "Programme created",
  "programme.created": "Programme created",
  "programme.curriculum-migrated": "Programme curriculum migrated",
  "programme.delivery-status-changed": "Programme delivery status changed",
  "programme.end-of-life-set": "Programme end of life set",
  "programme.export": "Programme exported",
  "programme.export-previewed": "Programme export previewed",
  "programme.exported": "Programme exported",
  "programme.governance-finding-created": "Governance finding raised",
  "programme.governance-finding-resolved": "Governance finding resolved",
  "programme.internal-review-skipped": "Internal review skipped",
  "programme.publication.published": "Programme published",
  "programme.publication.withdrawn": "Programme publication withdrawn",
  "programme.readiness-overridden": "Programme readiness overridden",
  "programme.replacement-created": "Replacement programme created",
  "programme.returned-to-draft": "Programme returned to Draft",
  "programme.review-recorded": "Programme review recorded",
  "programme.review-round-opened": "Programme review round opened",
  "programme.revised": "Programme revised",
  "programme.revised-from-snapshot": "Programme revised from snapshot",
  "programme.revision-applicability-created": "Revision applicability set",
  "programme.signed-off": "Programme signed off",
  "programme.snapshot-created": "Programme snapshot created",
  "programme.state_changed": "Programme state changed",
  "programme.updated": "Programme updated",
  "programme.visibility-changed": "Programme visibility changed",
  "programme.workflow-transitioned": "Programme state changed",
  "role.changed": "Role changed",
  "settings.access-policy.updated": "Access policy updated",
  "settings.provider.updated": "Provider settings updated",
  "settings.updated": "Settings updated",
  "snapshot.restored": "Snapshot restored",
  "support-access.enabled": "Support access enabled",
  "support-access.revoked": "Support access revoked",
  "support-access.used": "Support access used",
  "tenant-capabilities.changed": "Tenant capabilities changed",
  "tenant.created": "Tenant created",
  "tenant.entitlement.decided": "Tenant entitlement decided",
  "user.activated": "User activated",
  "user.deactivated": "User deactivated",
  "user.email-changed": "User email changed",
  "user.invited": "User invited",
  "workflow.transitioned": "Workflow state changed",
};

/** Words of an identifier as a sentence, for a type no entry names yet. */
function humanise(action: string): string {
  const words = action
    .split(/[.\-_]/)
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase();
  return words.charAt(0).toLocaleUpperCase() + words.slice(1);
}

/**
 * The label for an event type, falling back to its words as a sentence so an
 * unmapped type still never shows as an identifier.
 */
export function auditEventLabel(action: string): string {
  return (AUDIT_EVENT_LABELS as Partial<Record<string, string>>)[action] ?? humanise(action);
}
